/**
 * Qobuz catalog helpers for streaming album availability and local-library matching.
 */

import { CLOUD_API_URL } from '../config/serverConfig';
import { getHeaders } from '../lib/useQobuz';
import {
  getLocalAlbumIndexSnapshot,
  type AlbumIndexRow,
} from '../services/libraryAlbumIndex';
import { diagnosticLog } from './diagnosticLogger';

export interface QobuzAlbumCatalogSnapshot {
  albumId: string;
  title: string | null;
  artistName: string | null;
  coverUrl: string | null;
  albumStreamable: boolean;
  totalTracks: number;
  streamableTracks: number;
  fetched: boolean;
}

function normalizeTitle(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function normalizeArtist(value: unknown): string {
  let s = normalizeTitle(value);
  if (s.startsWith('the ')) s = s.slice(4);
  return s;
}

function artistsLikelySame(a: string, b: string): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length <= b.length ? b : a;
  if (shorter.length >= 4 && longer.includes(shorter)) return true;
  return false;
}

export function getQobuzStoreRegion(): string {
  try {
    const raw = localStorage.getItem('maestro_qobuz_user');
    if (!raw) return 'unknown';
    const user = JSON.parse(raw) as { country_code?: string; countryCode?: string };
    return user?.country_code || user?.countryCode || 'unknown';
  } catch {
    return 'unknown';
  }
}

export function countStreamableTracks(tracks: Array<{ streamable?: boolean }>): {
  total: number;
  streamable: number;
} {
  const total = tracks.length;
  const streamable = tracks.filter((t) => t.streamable !== false).length;
  return { total, streamable };
}

function extractTracksFromPayload(payload: Record<string, unknown> | null | undefined): Array<{ streamable?: boolean }> {
  if (!payload) return [];
  const tracksField = payload.tracks as unknown;
  if (Array.isArray(tracksField)) return tracksField as Array<{ streamable?: boolean }>;
  if (tracksField && typeof tracksField === 'object' && Array.isArray((tracksField as { items?: unknown[] }).items)) {
    return (tracksField as { items: Array<{ streamable?: boolean }> }).items;
  }
  return [];
}

function pickCoverUrl(...sources: Array<Record<string, unknown> | null | undefined>): string | null {
  for (const src of sources) {
    if (!src) continue;
    const image = src.image as { large?: string; small?: string } | string | undefined;
    const candidates = [
      src.coverUrl,
      src.cover_url,
      src.artworkUrl,
      typeof image === 'object' ? image?.large : image,
      typeof image === 'object' ? image?.small : null,
    ];
    for (const c of candidates) {
      const s = String(c || '').trim();
      if (s.startsWith('http')) return s;
    }
  }
  return null;
}

/** Fetch Qobuz album metadata + track streamability for streaming wall tiles and detail guards. */
export async function fetchQobuzAlbumCatalog(albumId: string): Promise<QobuzAlbumCatalogSnapshot> {
  const rawId = String(albumId || '').trim().replace(/^qobuz:album:/i, '');
  const empty: QobuzAlbumCatalogSnapshot = {
    albumId: rawId,
    title: null,
    artistName: null,
    coverUrl: null,
    albumStreamable: false,
    totalTracks: 0,
    streamableTracks: 0,
    fetched: false,
  };
  if (!rawId) return empty;

  try {
    const hdrs = getHeaders();
    const [albumResp, detailResp] = await Promise.all([
      fetch(`${CLOUD_API_URL}/api/streaming/qobuz/album?albumId=${encodeURIComponent(rawId)}`, { headers: hdrs }),
      fetch(`${CLOUD_API_URL}/api/streaming/qobuz/album-detail?album_id=${encodeURIComponent(rawId)}`, { headers: hdrs }),
    ]);

    const albumData = albumResp.ok ? await albumResp.json().catch(() => null) : null;
    const detailData = detailResp.ok ? await detailResp.json().catch(() => null) : null;

    const albumObj = (albumData?.album || (albumData?.id ? albumData : null)) as Record<string, unknown> | null;
    const detailObj = (detailData?.album || (detailData?.id ? detailData : detailData)) as Record<string, unknown> | null;
    const merged = { ...(detailObj || {}), ...(albumObj || {}) };

    const rawTracks = [
      ...extractTracksFromPayload(merged),
      ...extractTracksFromPayload(albumObj || undefined),
      ...extractTracksFromPayload(detailObj || undefined),
    ];
    const { total, streamable } = countStreamableTracks(rawTracks);

    const artistField = merged.artist as { name?: string } | string | undefined;
    const artistName = String(
      merged.artistName
      || merged.artist_name
      || (typeof artistField === 'object' ? artistField?.name : artistField)
      || '',
    ).trim() || null;

    return {
      albumId: rawId,
      title: String(merged.title || '').trim() || null,
      artistName,
      coverUrl: pickCoverUrl(merged, albumObj || undefined, detailObj || undefined),
      albumStreamable: merged.streamable !== false,
      totalTracks: total,
      streamableTracks: streamable,
      fetched: !!(merged.id || merged.title || total > 0),
    };
  } catch {
    return empty;
  }
}

export function isAlbumCatalogUnstreamable(snapshot: QobuzAlbumCatalogSnapshot): boolean {
  if (!snapshot.fetched) return false;
  if (snapshot.totalTracks === 0 && snapshot.albumStreamable === false) return true;
  if (snapshot.totalTracks > 0 && snapshot.streamableTracks === 0) return true;
  return false;
}

/** Match a streaming album to a local index row by canonical title + artist. */
export function findLocalAlbumMatch(title?: string | null, artist?: string | null): AlbumIndexRow | null {
  const targetTitle = normalizeTitle(title);
  const targetArtist = normalizeArtist(artist);
  if (!targetTitle) return null;

  const { albums } = getLocalAlbumIndexSnapshot();
  for (const row of albums) {
    const rowTitle = normalizeTitle(row.title ?? row.album);
    const rowArtist = normalizeArtist(row.artist_name ?? row.album_artist ?? row.artist);
    if (rowTitle !== targetTitle) continue;
    if (targetArtist && rowArtist && !artistsLikelySame(targetArtist, rowArtist)) continue;
    return row;
  }
  return null;
}

const _loggedUnavailableAlbums = new Set<string>();

/** One structured diagnostic line per album render with zero streamable tracks. */
export function logStreamingAlbumUnavailable(
  albumId: string,
  source: string,
  snapshot: Pick<QobuzAlbumCatalogSnapshot, 'totalTracks' | 'streamableTracks'>,
): void {
  const key = `${albumId}:${source}`;
  if (_loggedUnavailableAlbums.has(key)) return;
  _loggedUnavailableAlbums.add(key);

  diagnosticLog(
    'warn',
    'playback',
    `[streaming] album_unavailable_zero_streamable album=${albumId} region=${getQobuzStoreRegion()} source=${source} total_tracks=${snapshot.totalTracks} streamable_tracks=${snapshot.streamableTracks}`,
    {
      album_id: albumId,
      region: getQobuzStoreRegion(),
      source,
      total_tracks: snapshot.totalTracks,
      streamable_tracks: snapshot.streamableTracks,
    },
  );
}
